import { CollapsibleSection, useLanguage, useTranslate } from '@athena/ui'
import type { ReactNode } from 'react'

import { useFormat } from '../../lib/format.js'

import { shelve, type ShelfRow } from './studentShelf.js'

export interface StudentSubjectGroupsProps<T extends ShelfRow> {
  practices: readonly T[]
  renderPractice: (practice: T, presentation: 'card' | 'row') => ReactNode
}

/**
 * The student's practices, one fold per subject — opened for them where there
 * is something to do.
 *
 * The tutor's list folds every subject, because a tutor with forty practices
 * is scanning for one. A student is asking "is there anything for me?", and a
 * row of closed subjects cannot answer that: they had to open each to find
 * out. So a subject with something to start opens by itself (see `shelve` for
 * which, and how many), and every header says what is inside — "2 practices ·
 * 1 available" — so a folded one still answers the question.
 *
 * Which subjects start open is decided when the home first draws them.
 * `CollapsibleSection` owns its state from then on, so a subject the student
 * folded stays folded, and one they opened stays open, for as long as they
 * are on the page — a refetch that changes the counts does not undo their
 * choice. Nothing is remembered past that.
 */
export function StudentSubjectGroups<T extends ShelfRow>({
  practices,
  renderPractice,
}: StudentSubjectGroupsProps<T>) {
  const t = useTranslate()
  const format = useFormat()
  const language = useLanguage()
  const groups = shelve(practices, language)

  return (
    <div className="flex flex-col gap-6">
      {groups.map((group) => {
        // Small shelves remain a set of independent choices. Once a subject
        // has four or more practices, repeating cards becomes visual noise;
        // the same entities become one divided reading list instead.
        const presentation = group.practices.length >= 4 ? 'row' : 'card'

        return (
          <CollapsibleSection
            key={group.subject.id}
            title={group.subject.name}
            // How many there are, then whether any can be started — the one
            // thing a folded subject has to say. Two facts, never a dashboard.
            meta={`${format.count('practices', group.practices.length)} · ${
              group.available > 0
                ? format.count('available', group.available)
                : t('learn.group.noneAvailable')
            }`}
            defaultOpen={group.open}
          >
            <div
              className={
                presentation === 'card'
                  ? 'grid grid-cols-[repeat(auto-fit,minmax(min(100%,18rem),1fr))] gap-4'
                  : 'divide-y divide-border'
              }
              data-presentation={presentation === 'card' ? 'cards' : 'rows'}
            >
              {group.practices.map((practice) => renderPractice(practice, presentation))}
            </div>
          </CollapsibleSection>
        )
      })}
    </div>
  )
}
